import { startCase } from 'lodash'
import { BreadcrumbJsonLd } from 'next-seo'
import { Translate } from 'next-translate'
import useTranslation from 'next-translate/useTranslation'
import getConfig from 'next/config'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React from 'react'

import { useCities } from '@/context/Cities'
import { Zone } from '@/types/cities'
import { CanonicalFilter } from '@/types/filter'
import { getBreadcrumbList } from '@/utils/breadcrumb-list'
import { getAlternatePageLinksWithZone, getAlternatePageLinksWithCanonicalFilter } from '@/utils/getAlternatePageLinks'

import { getHousesForSalesPathSegment } from '../i18n-routes'

const getTitleAndDescription = (
  zone: Zone,
  t: Translate,
  tCity: string,
  canonicalFilterId: string
): {
  description: string
  title: string
} => {
  if (zone) {
    return {
      description: t('zones:seo-description', { city: tCity, zone: zone.label }),
      title: t('zones:seo-title', { city: tCity, zone: zone.label }),
    }
  } else if (canonicalFilterId) {
    return {
      description: t(`canonical-filters:${canonicalFilterId}.description`, { city: tCity }),
      title: t(`canonical-filters:${canonicalFilterId}.title`, { city: tCity }),
    }
  }

  return {
    description: t('common:description-listing', { city: tCity }),
    title: t('common:title-listing', { city: tCity }),
  }
}

type Props = {
  canonicalFilter?: CanonicalFilter
  zone?: Zone
}

const ListingsMapHead: React.FC<Props> = ({ canonicalFilter, zone }) => {
  const { publicRuntimeConfig } = getConfig()
  const { lang, t } = useTranslation()

  const router = useRouter()
  const cities = useCities()

  const canonicalFilterLabel = canonicalFilter && canonicalFilter.id ? canonicalFilter.id : ''

  // URL path segments
  const housesForSalesSegment = getHousesForSalesPathSegment(lang)
  const citySegment = Array.isArray(router.query.city) ? router.query.city[0] : router.query.city
  const zoneSegment = zone ? zone.slug.split('/')[2] : ''
  const canonicalFilterSegment = canonicalFilterLabel ? canonicalFilter.slug[lang] : ''
  const geoSegment = [citySegment, zoneSegment, canonicalFilterSegment].filter(Boolean).join('/')

  // URL
  const urlPath = '/' + [housesForSalesSegment, geoSegment].filter(Boolean).join('/') + '/'
  const canonicalUrl = `${publicRuntimeConfig.lisaWebsite}${urlPath}`

  // Title and Description
  const cityLabel = startCase(citySegment?.replace(/-/g, ' ').toLowerCase())
  const { description, title } = getTitleAndDescription(zone, t, cityLabel, canonicalFilterLabel)

  // Alternate links
  const { city, home, params, ...queryParams } = router.query
  const hasQueryParams = Object.keys(queryParams).length > 0
  const hasCanonicalFilterLabel = canonicalFilterLabel !== ''

  let alternateLinks = null
  if (!hasQueryParams) {
    if (hasCanonicalFilterLabel) {
      alternateLinks = getAlternatePageLinksWithCanonicalFilter(lang, citySegment, canonicalFilter)
    } else {
      alternateLinks = getAlternatePageLinksWithZone(lang, geoSegment)
    }
  }

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta content={description} name="description" />
        {alternateLinks}
        <link href={canonicalUrl} rel="canonical" />
        <meta content={lang} property="og:locale" />
        <meta content="article" property="og:type" />
        <meta content={title} property="og:title" />
        <meta content={description} property="og:description" />
        <meta content={canonicalUrl} property="og:url" />
        <meta content="Casavo" property="og:site_name" />
        <meta content="summary_large_image" name="twitter:card" />
        <meta content={description} name="twitter:description" />
        <meta content={title} name="twitter:title" />
      </Head>

      <BreadcrumbJsonLd
        itemListElements={getBreadcrumbList({
          cities: cities.list,
          city: cities.router.city || cities.router.area,
          depth: 'listings',
          language: lang,
          t,
          zone,
        })}
      />
    </>
  )
}

export default ListingsMapHead
